import { useMutation } from '@apollo/client';
import React, { useCallback, useState } from 'react';
import Modal, { ModalBody, ModalFooter } from '../../components/Modal';
import { Flex, Text } from 'rebass';
import Button from '../../components/Button/Button';
import PropTypes from 'prop-types';
import { Textarea } from '@rebass/forms';
import useNotify from '../../hooks/useNotify';
import { AssignmentStatus } from '../../types/assignment';
import { ApolloError } from '@apollo/client/errors';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import { gql } from '@/modes/company/gql';

type SetJobStatusModalProps = {
  onClose: (actionTaken?: boolean) => void;
  assignmentIds: string[] | readonly string[]; // JobIds
  status: AssignmentStatus;
};

const SET_JOB_STATUS = gql(`
  mutation SetAssignmentsStatus(
    $assignmentIds: [ID!]!
    $newStatus: AssignmentStatus!
    $comment: String
  ) {
    setInboundAssignmentsStatus(
      assignmentIds: $assignmentIds
      newStatus: $newStatus
      comment: $comment
    ) {
      id
      status
      due
      submitted
      canceled
      followUp
      completed
      assigned
      updated
    }
  }
`);

const SetJobStatusModal: React.FC<SetJobStatusModalProps> = ({
  onClose,
  status,
  assignmentIds,
}) => {
  const onCancel = useCallback(() => onClose(), []);
  const onActionComplete = useCallback(() => onClose(true), []);
  const { showError } = useNotify();

  const [SetJobStatus, { loading: mutationLoading }] = useMutation<
    any,
    {
      assignmentIds: string[] | readonly string[];
      newStatus: AssignmentStatus;
      comment?: string;
    }
  >(SET_JOB_STATUS, {
    onCompleted: onActionComplete,
    onError: useCallback((error: ApolloError) => {
      showError(
        `Error Setting Job${assignmentIds.length > 1 ? 's' : ''} status`,
        error
      );
    }, []),
  });

  const [comment, setComment] = useState<string>('');
  const onChange = useCallback(
    (e: any) => {
      setComment(e.target.value);
    },
    [setComment]
  );

  return (
    <Modal title={`Set Status`} onClose={onCancel}>
      <ModalBody>
        <Flex p={3} flexDirection="column">
          <Text pb={5} variant="body1">
            Set job
            {assignmentIds.length > 1 ? 's ' : ' '}
            status to "{constantToLabel(status)}"
          </Text>
          <Textarea
            height={3 * 16 + 2}
            placeholder="Enter optional comment"
            autoFocus
            onChange={onChange}
          />
          {mutationLoading && <p>Loading...</p>}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          icon="check-circle"
          disabled={mutationLoading}
          onClick={() => {
            SetJobStatus({
              variables: {
                assignmentIds,
                newStatus: status,
                comment,
              },
            });
          }}
        >
          Confirm
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onCancel}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

SetJobStatusModal.propTypes = {
  onClose: PropTypes.any,
  assignmentIds: PropTypes.any.isRequired,
  status: PropTypes.any.isRequired,
};

export default SetJobStatusModal;
